import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import compose from 'utils/compose';
import ReactDatePicker from 'react-datepicker';
import classNames from 'classnames';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { withStyles } from 'tss-react/mui';
import { withTheme } from '@emotion/react';
import Slide from '@mui/material/Slide';
import TextField from '@mui/material/TextField';
import { getPredefinedRange } from 'utils/date/utils';
import { DateTime } from 'luxon';

const styles = (theme) => ({
  customDateWrapper: {
    display: 'flex',
  },
  dateTextField: {
    fontSize: theme.typography.body2.fontSize,
  },
  endDate: {
    width: theme.spacing(12),
  },
  icon: {
    color: theme.applyOpacityToHex(theme.palette.greyScaleDeprecated[0], 0.87),
    top: 'calc(50% - 11px)',
    right: 8,
  },
  menuItemRoot: {
    fontSize: 14,
    minHeight: 32,
  },
  popper: {
    '& .react-datepicker__triangle': {
      left: '15em',
    },
  },
  root: {
    display: 'flex',
    alignItems: 'center',
    border: '1px solid #c9c7c5',
    borderRadius: 25,
  },
  selectFormControl: {
    marginRight: 15,
  },
  select: {
    color: theme.applyOpacityToHex(theme.palette.greyScaleDeprecated[0], 0.87),
    marginRight: 16,
    paddingLeft: 20,

    '&:focus': {
      backgroundColor: 'transparent',
    },
  },
  startDate: {
    marginRight: 15,
    width: theme.spacing(12),
  },
});

export const dateRanges = [
  'Month to Date',
  'This Month',
  'Last 30 Days',
  'Last Month',
  'Last 6 Months',
  'Last 12 Months',
  'Year to Date',
  'Custom',
];

export const overTimeDateRanges = [
  'Last 6 Months',
  'Last 12 Months',
];

export const intervalForRange = (range) => {
  switch (range.value) {
    case 'Last 30 Days': return getPredefinedRange({ value: 'LAST_30_DAYS' });
    case 'Month to Date': return getPredefinedRange({ value: 'MONTH_TO_TODAY' });
    case 'This Month': return getPredefinedRange({ value: 'THIS_MONTH' });
    case 'Last Month': return getPredefinedRange({ value: 'LAST_MONTH' });
    case 'Last 2 Months': return getPredefinedRange({ value: 'LAST_2_MONTHS' });
    case 'Last 6 Months': return getPredefinedRange({ value: 'LAST_6_MONTHS' });
    case 'Last 12 Months': return getPredefinedRange({ value: 'LAST_12_MONTHS' });
    case 'Year to Date': return getPredefinedRange({ value: 'YEAR_TO_TODAY' });
    case 'Custom': return getPredefinedRange({ ...range, value: 'CUSTOM' });
    default: return getPredefinedRange({ value: 'LAST_30_DAYS' });
  }
};

class DateRangeMenu extends PureComponent {

  state = {
    startDate: DateTime.local().minus({ months: 1 }).toJSDate(),
    endDate: new Date(),
  };

  static propTypes = {
    currentDateRange: PropTypes.object,
    classes: PropTypes.object,
    onRangeSelect: PropTypes.func,
    fontSize: PropTypes.number,
    style: PropTypes.object,
    showOnlyLastSixOrTwelveMonths: PropTypes.bool,
  };

  static defaultProps = {
    fontSize: 13,
    style: {},
  };

  //   static getDerivedStateFromProps(nextProps, prevState) {
  //     const { currentDateRange: { startDate, endDate } } = nextProps;
  // debugger;
  //     if (startDate && !prevState.startDate.isSame(moment(startDate))) {
  //       return {
  //         startDate: moment(startDate),
  //         endDate: moment(endDate),
  //       };
  //     }
  //
  //     return null;
  //   }

  rangeToId = (range) => `range-${range?.toLowerCase().replace(/[ ]/g, '-')}`;

  onChangeDate = (jsDate, isStartDate) => {
    const { onRangeSelect } = this.props;
    let { startDate, endDate } = this.state;

    if (isStartDate) {
      this.setState({ startDate: jsDate });
      startDate = jsDate;
    } else {
      this.setState({ endDate: jsDate });
      endDate = jsDate;
    }

    onRangeSelect({ value: 'Custom', startDate, endDate });
  };

  onBlurCustomDate = (e, isStartDate) => {
    if (!DateTime.fromFormat(e.target.value, 'D').isValid) {
      if (isStartDate) {
        this.onChangeDate(DateTime.local().minus({ month: 1 }).toJSDate(), true);
      } else {
        this.onChangeDate(new Date(), false);
      }
    }
  };

  render() {
    const nameOfLastMonth = DateTime.local().minus({ month: 1 }).monthLong;
    const { currentDateRange, classes, onRangeSelect, fontSize, style, showOnlyLastSixOrTwelveMonths } = this.props;
    const ranges = showOnlyLastSixOrTwelveMonths ? overTimeDateRanges : dateRanges;
    const { startDate, endDate } = this.state;
    const isCustomDate = currentDateRange.value === 'Custom';

    return (
      <div className={classes.root}>
        <FormControl id="date-range-select" className={classNames({ [classes.selectFormControl]: isCustomDate })}>
          <Select
            value={currentDateRange.value}
            onChange={(e) => onRangeSelect({ value: e.target.value, startDate, endDate })}
            classes={{
              select: classes.select,
              icon: classes.icon,
              filled: classes.filled,
            }}
            style={({ ...style, position: 'relative', top: 1, fontSize })}
            id="spending-date-range"
            disableUnderline
            MenuProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              getContentAnchorEl: null,
              onExited: () => document.activeElement.blur(),
            }}
          >
            {ranges.map((range) => (
              <MenuItem
                key={range}
                value={range}
                id={this.rangeToId(range)}
                classes={{
                  root: classes.menuItemRoot,
                }}
              >
                {range === 'Last Month' ? `${range} (${nameOfLastMonth})` : range}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Slide direction="left" in={isCustomDate} mountOnEnter unmountOnExit transition={800}>
          <div className={classes.customDateWrapper}>
            <ReactDatePicker
              id="custom-start-date"
              key={startDate}
              className={classes.startDate}
              selected={startDate}
              onChange={(jsDate) => {
                if (jsDate) {
                  this.onChangeDate(jsDate, true);
                }
              }}
              onBlur={(e) => this.onBlurCustomDate(e, true)}
              popperPlacement="bottom-start"
              popperModifiers={{
                preventOverflow: {
                  enabled: true,
                  escapeWithReference: false,
                  boundariesElement: 'viewport',
                },
              }}
              customInput={
                <TextField
                  InputProps={{ classes: { input: classes.dateTextField } }}
                  InputLabelProps={{ classes: { root: classes.dateTextField } }}
                  label="Start Date"
                  margin="none"
                />
              }
            />
            <ReactDatePicker
              id="custom-end-date"
              key={endDate}
              selected={endDate}
              className={classes.endDate}
              onChange={(jsDate) => {
                if (jsDate) {
                  this.onChangeDate(jsDate, false);
                }
              }}
              onBlur={(e) => this.onBlurCustomDate(e, false)}
              popperClassName={classes.popper}
              popperModifiers={{
                preventOverflow: {
                  enabled: true,
                  escapeWithReference: false,
                  boundariesElement: 'viewport',
                },
              }}
              customInput={
                <TextField
                  InputProps={{ classes: { input: classes.dateTextField } }}
                  InputLabelProps={{ classes: { root: classes.dateTextField } }}
                  label="End Date"
                  margin="none"
                />
              }
            />
          </div>
        </Slide>
      </div>
    );
  }
}
export default compose(
  withTheme,
)(withStyles(DateRangeMenu, styles));
